import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Bell, BellDot, CheckCheck, Info, AlertTriangle, XCircle, CheckCircle, BellRing, BellOff } from 'lucide-react'
import Swal from 'sweetalert2'
import { notificationsService, type AppNotification } from '../services/notifications.service'
import { pushService } from '../services/push.service'
import { useLang } from '../context/LangContext'
import { useAdminContext } from '../context/AdminContext'
import { useNavigate } from 'react-router-dom'

const POLL_INTERVAL = 30_000

function severityIcon(sev: AppNotification['severity']) {
  const size = 14
  switch (sev) {
    case 'error':   return <XCircle       size={size} style={{ color: 'var(--color-error, #dc2626)'   }} />
    case 'warning': return <AlertTriangle size={size} style={{ color: 'var(--color-warning, #f59e0b)' }} />
    case 'success': return <CheckCircle   size={size} style={{ color: 'var(--color-success, #16a34a)' }} />
    default:        return <Info          size={size} style={{ color: 'var(--color-info, #3b82f6)'    }} />
  }
}

function timeAgo(iso: string, n: { now: string; minutesAgo: string; hoursAgo: string; daysAgo: string }): string {
  const diff  = Date.now() - new Date(iso).getTime()
  const mins  = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days  = Math.floor(diff / 86_400_000)
  if (mins  < 1)  return n.now
  if (mins  < 60) return n.minutesAgo.replace('{n}', String(mins))
  if (hours < 24) return n.hoursAgo.replace('{n}', String(hours))
  return n.daysAgo.replace('{n}', String(days))
}

interface Props {
  isAdmin: boolean
}

export const NotificationBell: React.FC<Props> = ({ isAdmin }) => {
  const { t } = useLang()
  const nt = t.notifications
  const { hasPermission } = useAdminContext()

  // ── Push (PWA) ──
  const canUsePush = pushService.isSupported() && (isAdmin || hasPermission('receive_push'))
  const [pushOn, setPushOn] = useState(false)
  const [pushBusy, setPushBusy] = useState(false)

  const [unreadCount, setUnreadCount] = useState(0)
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({})
  const panelRef = useRef<HTMLDivElement>(null)
  const btnRef   = useRef<HTMLButtonElement>(null)

  // ── Poll unread count ──────────────────────────────────────────────────────
  const fetchCount = useCallback(async () => {
    try {
      const count = await notificationsService.getCount()
      setUnreadCount(count)
    } catch { /* silently ignore */ }
  }, [])

  useEffect(() => {
    fetchCount()
    const id = setInterval(fetchCount, POLL_INTERVAL)
    return () => clearInterval(id)
  }, [fetchCount])

  // ── Estado de la suscripción push de este dispositivo ──
  useEffect(() => {
    if (canUsePush) pushService.isSubscribed().then(setPushOn)
  }, [canUsePush])

  const togglePush = async () => {
    setPushBusy(true)
    try {
      if (pushOn) { await pushService.disable(); setPushOn(false) }
      else        { await pushService.enable();  setPushOn(true) }
    } catch (e: any) {
      const denied = e?.message === 'denied'
      Swal.fire({
        icon: denied ? 'warning' : 'error',
        title: denied ? (nt.pushDenied ?? 'Permiso bloqueado') : 'Error',
        text: denied
          ? (nt.pushDeniedHint ?? 'Activá las notificaciones para este sitio en la configuración del navegador.')
          : (nt.pushError ?? 'No se pudo activar el push.'),
        confirmButtonColor: '#2c92e6',
      })
    } finally {
      setPushBusy(false)
    }
  }

  // ── Compute panel position from button rect ────────────────────────────────
  const computePanelStyle = useCallback(() => {
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    const vw = window.innerWidth

    if (vw <= 600) {
      // Mobile: centered, fixed below the button
      const panelW = Math.min(vw * 0.92, 360)
      setPanelStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        left: (vw - panelW) / 2,
        width: panelW,
        maxHeight: '70vh',
      })
    } else {
      // Desktop: anchored to right edge of button
      setPanelStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        right: vw - rect.right,
        width: 360,
        maxHeight: 480,
      })
    }
  }, [])

  // ── Open panel: compute position + load list ──────────────────────────────
  const handleOpen = async () => {
    if (open) { setOpen(false); return }
    computePanelStyle()
    setOpen(true)
    setLoading(true)
    try {
      const list = await notificationsService.getAll()
      setNotifications(list)
    } catch {
      setNotifications([])
    } finally {
      setLoading(false)
    }
  }

  // ── Close on outside click ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        btnRef.current   && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // ── Mark single as read ───────────────────────────────────────────────────
  /**
   * A dónde lleva una notificación.
   *
   * Se deriva del tipo y del `meta` y NO de un campo `url` guardado, para que
   * funcione también con las notificaciones viejas —que no lo tienen— y para que
   * un cambio de rutas no deje enlaces muertos en la base.
   *
   * Devuelve null cuando no hay a dónde ir (avisos de sistema): ahí la
   * notificación se marca leída y nada más, como hasta ahora.
   */
  const destinoDe = (n: AppNotification): string | null => {
    let meta: any = null
    try { meta = n.meta ? JSON.parse(n.meta) : null } catch { /* meta ilegible */ }
    if (!meta) return null

    // Una grabación concreta: se abre la campaña y dentro, la ficha.
    if (meta.callId && meta.campaignId) {
      return `/campaigns?open=${meta.campaignId}&call=${meta.callId}`
    }
    // Un seguimiento: la ficha del operador en Operadores.
    if (meta.usuario) {
      const qs = new URLSearchParams({ usuario: String(meta.usuario) })
      if (meta.periodo) qs.set('periodo', String(meta.periodo))
      return `/operators?${qs.toString()}`
    }
    // Una campaña (desactivada por límite o a mano).
    if (meta.campaignId) return `/campaigns?open=${meta.campaignId}`
    return null
  }

  const abrirNotificacion = async (n: AppNotification) => {
    if (!n.isRead) await markRead(n.id)
    const destino = destinoDe(n)
    if (destino) { setOpen(false); navigate(destino) }
  }

  const markRead = async (id: string) => {
    await notificationsService.markRead(id)
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n))
    setUnreadCount(prev => Math.max(0, prev - 1))
  }

  // ── Mark all as read ──────────────────────────────────────────────────────
  const markAll = async () => {
    await notificationsService.markAllRead()
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
    setUnreadCount(0)
  }

  const panel = open ? (
    <div
      ref={panelRef}
      className="notif-panel"
      style={panelStyle}
    >
      <div className="notif-panel__header">
        <span className="notif-panel__title">{nt.title}</span>
        {unreadCount > 0 && (
          <button className="notif-panel__mark-all" onClick={markAll} title={nt.markAllTitle}>
            <CheckCheck size={14} />
            <span>{nt.markAll}</span>
          </button>
        )}
      </div>

      {canUsePush && (
        <button
          onClick={togglePush}
          disabled={pushBusy}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            padding: '0.55rem 0.875rem', border: 'none', cursor: pushBusy ? 'default' : 'pointer',
            borderBottom: '1px solid var(--border-color)', textAlign: 'left',
            background: pushOn ? 'rgba(22,163,74,0.08)' : 'transparent',
            color: pushOn ? 'var(--color-success, #16a34a)' : 'var(--text-secondary)',
            fontSize: '0.8rem', fontWeight: 600,
          }}
          title={pushOn ? (nt.pushOnTitle ?? 'Notificaciones push activas en este dispositivo') : (nt.pushOffTitle ?? 'Activar push en este dispositivo')}
        >
          {pushOn ? <BellRing size={15} /> : <BellOff size={15} />}
          <span>{pushBusy ? (nt.loading ?? '...') : pushOn ? (nt.pushOn ?? 'Push activado en este dispositivo') : (nt.pushOff ?? 'Activar push en este dispositivo')}</span>
        </button>
      )}

      <div className="notif-panel__list">
        {loading && <div className="notif-panel__empty">{nt.loading}</div>}
        {!loading && notifications.length === 0 && (
          <div className="notif-panel__empty">{nt.empty}</div>
        )}
        {!loading && notifications.map(n => (
          <div
            key={n.id}
            className={`notif-item${n.isRead ? ' notif-item--read' : ''}`}
            onClick={() => abrirNotificacion(n)}
            style={destinoDe(n) ? { cursor: 'pointer' } : undefined}
            title={destinoDe(n) ? nt.openHint : undefined}
          >
            <div className="notif-item__icon">{severityIcon(n.severity)}</div>
            <div className="notif-item__body">
              <p className="notif-item__title">{n.title}</p>
              <p className="notif-item__msg">{n.message}</p>
              <span className="notif-item__time">{timeAgo(n.createdAt, nt)}</span>
            </div>
            {!n.isRead && <span className="notif-item__dot" />}
          </div>
        ))}
      </div>
    </div>
  ) : null

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        className="admin-header__theme-toggle notif-bell__btn"
        onClick={handleOpen}
        aria-label={`${nt.title}${unreadCount ? ` (${unreadCount} ${nt.unread})` : ''}`}
        title={nt.title}
      >
        {unreadCount > 0 ? <BellDot size={16} /> : <Bell size={16} />}
        {unreadCount > 0 && (
          <span className="notif-bell__badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {createPortal(panel, document.body)}
    </div>
  )
}
